import { useEffect, useMemo, useRef } from 'react';
import type { InvitationActivateResponse, InvitationApiError } from '@bingads-webui-campaign-react/accept-invitation-api';
import { useAcceptInvitationLogger } from './use-accept-invitation-logger';

// Reduces an InvitationApiError to a single stable token for the telemetry `errorCode` field.
// Prefers the specific CCMT code in `errorCodes` over the generic top-level `error` wrapper
// (e.g. `InvitationValidationFailed` / `ActivationFailed`), so the error-count report shows the
// real cause (e.g. `UserInvitationCodeExpired`) instead of the wrapper, and lines up with the
// server's raw-log error codes. Falls back to the wrapper, then the HTTP status, then `Unknown`.
const toErrorCode = (error?: InvitationApiError): string => {
  if (!error) {
    return 'Unknown';
  }
  if (Array.isArray(error.errorCodes) && error.errorCodes.length > 0) {
    // Surface a fraud/blocked CCMT code (verified in logs: CCMTErrorNumber_FraudUserIsBlocked) even
    // when it isn't first, so a fraud failure is never masked by a co-reported code. Matched by the
    // spelling-agnostic /fraud/i so any future fraud/blocked code variant is still caught.
    const fraudCode = error.errorCodes.find(candidate => /fraud/i.test(candidate));
    return String(fraudCode || error.errorCodes[0]);
  }
  if (error.error) {
    return error.error;
  }
  if (error.status !== undefined) {
    return `HTTP${error.status}`;
  }
  return 'Unknown';
};

// PII-safe structured diagnostics from a non-2xx JSON error body, attached to the telemetry line so a
// report can drill into the FULL server error — the primary `errorCode` carries only the single top
// code. Keys are chosen so the report's series decode (which keys ONLY on reason/errorCode/type)
// ignores them, leaving the existing `<outcome>:<errorCode>` labels unchanged. The raw `message` is
// intentionally omitted (it can carry PII) — join to the server-side log by SesID for the full text.
const toErrorExtra = (error?: InvitationApiError): Record<string, string | number | undefined> => {
  if (!error) {
    return {};
  }
  return {
    httpStatus: error.status,
    // The generic wrapper (e.g. ActivationOptionsFailed / InvitationValidationFailed) — context for
    // when `errorCode` is a specific CCMT code rather than the wrapper.
    errorType: error.error,
    // The full CCMT code list; `errorCode` already carries the first, so only add when there are more.
    errorCodes: error.errorCodes && error.errorCodes.length > 1 ? error.errorCodes.join('|') : undefined,
    // Response content-type + final (post-redirect) URL on a non-contract 2xx (NonJsonResponse), so an
    // Activate:Failed line can name the cause (e.g. redirected to /error) like the ActivationOptions path.
    contentType: error.contentType,
    url: error.url,
  };
};

// The resolution flags that decide which post-auth screen the user landed on. Mirrors the render
// branches in the orchestrator so the funnel report can attribute each entered session to an outcome.
export type PostAuthResolutionInput = {
  code: string;
  debugErrorPage: boolean;
  // True when the user picked "Create an account" pre-auth (URL `intent=create`). Reported on the
  // entry line so the `ExistingProfileDialog` rate has an exact denominator: the dialog can only
  // appear for create-intent sessions, so all-post-auth overstates the base by ~3.5x. Boolean, not
  // the raw query value, so an arbitrary user-supplied `intent` never reaches the log.
  isCreateIntent: boolean;
  isLoading: boolean;
  sessionExpired: boolean;
  // The server's own code for the 401 that set `sessionExpired` — `ReauthRequired` (recoverable via a
  // silent challenge round-trip) vs `NotAuthenticated` (really signed out). Kept separate so the
  // funnel report can measure how many `SessionExpired` sessions the re-auth bounce actually saves.
  // Defaults to `NotAuthenticated` when the server sent a bare 401.
  sessionExpiredErrorCode?: string;
  validationError?: InvitationApiError;
  activationOptionsError?: InvitationApiError;
  activationOptionsData?: {
    type?: string;
    reason?: string;
    existingUser?: { address?: Record<string, string | undefined> };
  } | undefined;
  currentIdentityType?: string;
  activationResponse: InvitationActivateResponse | null;
  isAadOnlyAccountRequired: boolean;
  isHappyPathActivation: boolean;
  isConsentOnlyActivation: boolean;
  showExistingProfileDialog: boolean;
  isWorkAccountRequired: boolean;
  isPersonalAccountRequired: boolean;
  isGoogleAccountRequired: boolean;
  isFacebookAccountRequired: boolean;
  isAlternateAccountMerge: boolean;
  isAlternateAccountBlocked: boolean;
  isActivationDenied: boolean;
  isAlreadyHasAccess: boolean;
  isUpgradeAndActivate: boolean;
};

// The action a user took on a post-auth error page or dialog. One token per distinct button across
// every error screen; the screen itself is stamped separately, so a handler shared by several
// screens (e.g. "Use a different email") stays a single action.
export type InvitationScreenAction =
  | 'UseExistingProfile'
  | 'UseDifferentEmail'
  | 'GoToAccount'
  | 'BackToInvitation'
  | 'ContinueWithWorkAccount'
  | 'ContinueWithPersonalAccount'
  | 'SignInWithGoogle'
  | 'SignInWithFacebook'
  | 'ContinueAsAlternateAccount';

export type AcceptInvitationTelemetry = {
  // eslint-disable-next-line no-unused-vars
  logActivateSuccess: (response: InvitationActivateResponse, identityType?: string) => void;
  // eslint-disable-next-line no-unused-vars
  logActivateFailed: (error: InvitationApiError, identityType?: string) => void;
  // eslint-disable-next-line no-unused-vars
  logUnmappedRole: (roleName: string) => void;
  // eslint-disable-next-line no-unused-vars
  logSignOut: (fromScreen: string) => void;
  // eslint-disable-next-line no-unused-vars
  logScreenAction: (action: InvitationScreenAction) => void;
};

// Owns all post-auth telemetry emits so the orchestrator stays focused on rendering. Emits the
// shared `InvitationIssues::Result step=… outcome=… errorCode=… code=…` schema (mirroring the
// server-side raw logs) through the accept-invitation logger, so the success-rate + error-count
// reports parse client and server the same way. Every line carries `code` so a report can dedupe
// per session (SesID) or per invitation code. All lines are `trace` (never `error`) — error cases
// are identified from the `outcome`/`errorCode` fields, not the log level, so monitoring alerts
// aren't triggered.
// How this mount was reached: 'reload' (F5), 'back_forward' (history), or 'navigate' (fresh entry,
// e.g. re-clicking the invitation link). Re-entry resets form state, so this distinguishes the
// causes without guessing. Returns '' when the Navigation Timing entry is unavailable.
const getNavigationType = (): string => {
  try {
    const [entry] = (window.performance?.getEntriesByType?.('navigation') || []) as PerformanceNavigationTiming[];
    return entry?.type || '';
  } catch {
    return '';
  }
};

// Counts how many address fields the ActivationOptions existing-user record can pre-fill on the
// profile form. Only the COUNT is logged — the values are PII. A MultiuserActivation only reaches
// the form when its address is incomplete (a complete one skips straight to consent-only), so this
// measures how much of that form we can actually fill for the user.
const PREFILLABLE_ADDRESS_FIELDS = ['addressLine1', 'addressLine2', 'city', 'stateOrProvince', 'zipOrPostalCode', 'country'];

const countPrefillableAddressFields = (address?: Record<string, string | undefined>): number => (
  address ? PREFILLABLE_ADDRESS_FIELDS.filter(field => Boolean(address[field])).length : 0
);

export const useAcceptInvitationTelemetry = (input: PostAuthResolutionInput): AcceptInvitationTelemetry => {
  const {
    code, debugErrorPage, isCreateIntent, isLoading, sessionExpired, sessionExpiredErrorCode, validationError, activationOptionsError,
    activationOptionsData, currentIdentityType, activationResponse,
    isAadOnlyAccountRequired, isHappyPathActivation, isConsentOnlyActivation, showExistingProfileDialog,
    isWorkAccountRequired, isPersonalAccountRequired, isGoogleAccountRequired, isFacebookAccountRequired,
    isAlternateAccountMerge, isAlternateAccountBlocked,
    isActivationDenied, isAlreadyHasAccess, isUpgradeAndActivate,
  } = input;

  const logEvent = useAcceptInvitationLogger(code);

  const pageLoadedLoggedRef = useRef(false);
  const resolvedOutcomeLoggedRef = useRef<string | null>(null);
  const confirmationLoggedRef = useRef(false);

  // Resolves — once Validate + ActivationOptions settle — which post-auth outcome the user landed
  // on. Returns null while still loading (nothing to report yet).
  const postAuthResolution = useMemo<{
    outcome: string;
    errorCode?: string;
    // Extra diagnostic key=values for THIS outcome, merged onto the base line (e.g. the response
    // content-type + redirected URL for a non-contract 200).
    extra?: Record<string, string | number | boolean | null | undefined>;
  } | null>(() => {
    if (debugErrorPage || isLoading) {
      return null;
    }
    if (sessionExpired) {
      return { outcome: 'SessionExpired', errorCode: sessionExpiredErrorCode || 'NotAuthenticated' };
    }
    if (validationError) {
      return { outcome: 'ValidateFailed', errorCode: toErrorCode(validationError), extra: toErrorExtra(validationError) };
    }
    if (activationOptionsError) {
      // A non-contract 200 (non-JSON body: auth/login HTML, CDN/stale page, or a redirect that never
      // reached our API) is NOT a CCMT ActivationOptions failure — bucket it separately with the
      // response diagnostics so it's joinable to a server log and doesn't hide inside `EmptyType`.
      if (activationOptionsError.error === 'NonJsonResponse') {
        return {
          outcome: 'Fallback',
          errorCode: 'NonJsonResponse',
          extra: { contentType: activationOptionsError.contentType, url: activationOptionsError.url },
        };
      }
      return { outcome: 'ActivationOptionsFailed', errorCode: toErrorCode(activationOptionsError), extra: toErrorExtra(activationOptionsError) };
    }
    if (isAadOnlyAccountRequired) {
      return { outcome: 'AadOnlyRequired' };
    }
    if (isHappyPathActivation && isConsentOnlyActivation) {
      return { outcome: showExistingProfileDialog ? 'ExistingProfileDialog' : 'ConsentOnlyActivation' };
    }
    if (isHappyPathActivation) {
      return {
        outcome: 'ProfileForm',
        extra: { prefillAddr: countPrefillableAddressFields(activationOptionsData?.existingUser?.address) },
      };
    }
    if (isWorkAccountRequired) {
      return { outcome: 'WorkAccountRequired' };
    }
    if (isPersonalAccountRequired) {
      return { outcome: 'PersonalAccountRequired' };
    }
    if (isGoogleAccountRequired) {
      return { outcome: 'GoogleAccountRequired' };
    }
    if (isFacebookAccountRequired) {
      return { outcome: 'FacebookAccountRequired' };
    }
    if (isAlternateAccountMerge) {
      return { outcome: 'AlternateAccountMerge' };
    }
    if (isAlternateAccountBlocked) {
      return { outcome: 'AlternateAccountBlocked' };
    }
    // More specific than ActivationDenied — must be checked first (see orchestrator render order).
    if (isAlreadyHasAccess) {
      return { outcome: 'AlreadyHasAccess' };
    }
    if (isActivationDenied) {
      return { outcome: 'ActivationDenied' };
    }
    if (isUpgradeAndActivate) {
      return { outcome: 'UpgradeAndActivate' };
    }
    if (activationOptionsData) {
      // The report must NEVER show a bare `Fallback` — falling back is only a UI behavior, so the
      // error line has to say WHY we couldn't route the ActivationOptions payload. We encode the
      // reason directly into `errorCode` (self-describing on its own field, not reliant on the query
      // also reading `type`/`reason`, and robust because the log formatter drops empty `type` extras):
      //   - empty/missing `type`      -> `EmptyType`  (server contract violation: 200 with no type)
      //   - known/unknown `type` + a CCMT `reason` we couldn't route -> `UnmappedType_<type>_<reason>`
      //   - `type` present, no reason -> `UnmappedType_<type>`
      // Empirically (Kusto) the dominant case is `EmptyType`.
      const activationType = activationOptionsData.type;
      const { reason } = activationOptionsData;
      let errorCode = 'EmptyType';
      if (activationType) {
        errorCode = reason ? `UnmappedType_${activationType}_${reason}` : `UnmappedType_${activationType}`;
      }
      return { outcome: 'Fallback', errorCode };
    }
    return null;
  }, [
    debugErrorPage, isLoading, sessionExpired, sessionExpiredErrorCode, validationError, activationOptionsError,
    isAadOnlyAccountRequired, isHappyPathActivation, isConsentOnlyActivation, showExistingProfileDialog,
    isWorkAccountRequired, isPersonalAccountRequired, isGoogleAccountRequired, isFacebookAccountRequired,
    isAlternateAccountMerge, isAlternateAccountBlocked,
    isActivationDenied, isAlreadyHasAccess, isUpgradeAndActivate, activationOptionsData,
  ]);

  // Post-auth entry point (report denominator): the page mounted with an invitation code. Skipped
  // in debug-preview mode. Logged once; repeat mounts within a session dedupe on SesID at report time.
  useEffect(() => {
    if (debugErrorPage || !code || pageLoadedLoggedRef.current) {
      return;
    }
    pageLoadedLoggedRef.current = true;
    logEvent('PostAuth', 'PageLoaded', {
      extra: { navType: getNavigationType(), intent: isCreateIntent ? 'create' : undefined },
    });
  }, [debugErrorPage, code, isCreateIntent, logEvent]);

  // The terminal post-auth outcome (which screen the user saw / which error occurred). Logged once
  // per distinct resolution so a report can attribute every entered session to an outcome.
  useEffect(() => {
    if (!postAuthResolution) {
      return;
    }
    const key = `${postAuthResolution.outcome}:${postAuthResolution.errorCode || ''}`;
    if (resolvedOutcomeLoggedRef.current === key) {
      return;
    }
    resolvedOutcomeLoggedRef.current = key;
    logEvent('PostAuth', postAuthResolution.outcome, {
      errorCode: postAuthResolution.errorCode,
      // `type` decodes the generic `Fallback` (unmapped ActivationOptions type) and disambiguates the
      // reason-keyed error outcomes; `reason` is the CCMT ResultCode. `postAuthResolution.extra` adds
      // outcome-specific diagnostics (e.g. content-type + URL for a non-contract 200).
      extra: {
        type: activationOptionsData?.type,
        reason: activationOptionsData?.reason,
        idP: currentIdentityType,
        ...postAuthResolution.extra,
      },
    });
  }, [postAuthResolution, logEvent, activationOptionsData, currentIdentityType]);

  // Confirmation page reached — a backstop "flow finished" signal alongside the Activate success
  // (and the restore-from-refresh path, where no fresh Activate fires). Logged once.
  useEffect(() => {
    if (activationResponse && !confirmationLoggedRef.current) {
      confirmationLoggedRef.current = true;
      logEvent('Confirmation', 'Landed', { extra: { customerId: activationResponse.customerId } });
    }
  }, [activationResponse, logEvent]);

  return useMemo<AcceptInvitationTelemetry>(() => ({
    // The canonical "flow finished" signal (report numerator). Every activate path (form submit,
    // consent-only auto-activate, "use existing profile") funnels through here.
    logActivateSuccess: (response, identityType) => logEvent('Activate', 'Success', {
      extra: { customerId: response.customerId, idP: identityType },
    }),
    logActivateFailed: (error, identityType) => logEvent('Activate', 'Failed', {
      errorCode: toErrorCode(error),
      extra: { idP: identityType, ...toErrorExtra(error) },
    }),
    // The confirmation page rendered a roleName the client doesn't map (fell back to "Standard").
    // The unmapped value rides in `errorCode` so the report surfaces exactly which new/renamed
    // server role to add to the mapping.
    logUnmappedRole: roleName => logEvent('Confirmation', 'UnmappedRole', {
      errorCode: roleName,
    }),
    // The user deliberately signed out of a post-auth screen rather than finishing. Separates an
    // intentional exit from a silent drop-off, which otherwise land in the same abandoned bucket.
    logSignOut: fromScreen => logEvent('PostAuth', 'SignedOut', {
      extra: { from: fromScreen },
    }),
    // A button press on an error page or dialog. `screen` is taken from the already-resolved post-auth
    // outcome rather than the call site, so every screen is labelled consistently and a shared handler
    // is still attributable. Emitted under its own `ScreenAction` outcome so it is inert to the
    // existing success-rate / error-count reports (which key on the screen outcomes) until a query
    // opts in. Best effort: the emit races the navigation most of these actions start.
    //
    // Swallows its own failure: every caller emits BEFORE navigating away (sign-out, federated
    // sign-in, switch-email), so letting a telemetry fault propagate would abort the click handler
    // and strand the user on an error page with a dead button. Analytics must never cost a user
    // their only way forward. Mirrors useInvitationFormLogger's guard.
    logScreenAction: (action) => {
      if (debugErrorPage) {
        return;
      }
      try {
        logEvent('PostAuth', 'ScreenAction', {
          extra: { screen: postAuthResolution?.outcome || 'Unresolved', action },
        });
      } catch {
        // Intentionally ignored — see above.
      }
    },
  }), [logEvent, debugErrorPage, postAuthResolution]);
};
